import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  Paper, Typography, Box, FormControlLabel, Checkbox, TextField, Button, Alert, CircularProgress,
} from '@mui/material';
import { auth, db } from '../firebase';
import { readSignupIntent, parsePicks } from '../utils/signupIntent';

// "What brings you to Torch?" for a newly registered adult. Adults were
// registering and then hearing nothing; each answer here goes somewhere it is
// acted on (see submitAdultInterests): training texts the club, league joins
// the adult league interest list, membership goes to the member plans, and a
// question is emailed to the club.
const OPTIONS = [
  { key: 'training', label: 'Adult beach training', hint: 'We’ll add you to our training group chat.' },
  { key: 'league', label: 'The adult 4 on 4 league', hint: 'We’ll put you on the league list and be in touch.' },
  { key: 'member', label: 'Becoming a member / court reservations', hint: 'See the member plans next.' },
  { key: 'info', label: 'I just need more information', hint: 'Ask us anything below.' },
];

// onDismiss: shown as a "Not now" button where the card is a banner (What's New).
export default function AdultNextSteps({ onDismiss, onDone }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [picked, setPicked] = useState({ training: false, league: false, member: false, info: false });
  const [question, setQuestion] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(null);

  // What starts ticked: their answers from last time if there are any, so a
  // second visit is a change rather than a repeat; otherwise the choice they
  // clicked in the welcome email (?pick=), or the page they were looking at
  // before signing up (Adult Training or Memberships).
  useEffect(() => {
    const preset = (picks) => setPicked(p => {
      const next = { ...p };
      picks.forEach(k => { next[k] = true; });
      return next;
    });
    const fromLink = parsePicks(new URLSearchParams(location.search).get('pick'));
    const fallback = fromLink.length ? fromLink : readSignupIntent();
    const uid = auth.currentUser?.uid;
    if (!uid) { preset(fallback); return; }
    getDoc(doc(db, 'adultInterests', uid)).then(snap => {
      const d = snap.exists() ? snap.data() : {};
      if (d.training || d.league || d.member || d.info) {
        setPicked({ training: !!d.training, league: !!d.league, member: !!d.member, info: !!d.info });
        if (d.question) setQuestion(d.question);
      } else {
        preset(fallback);
      }
    }).catch(() => preset(fallback));
  }, [location.search]);

  const submit = async () => {
    setBusy(true); setError('');
    try {
      const { data } = await httpsCallable(getFunctions(), 'submitAdultInterests')({ ...picked, question });
      setSent(data);
      if (onDone) onDone(data);
    } catch (e) {
      setError(e.message || 'That did not go through. Please try again.');
    }
    setBusy(false);
  };

  const any = Object.values(picked).some(Boolean);

  return (
    <Paper elevation={1} sx={{ p: 3, borderRadius: 3, mb: 3, border: '2px solid #C9A84C' }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 0.5 }}>
        What brings you to Torch?
      </Typography>
      <Typography variant="body2" sx={{ color: '#666', mb: 1.5 }}>
        Pick one or more and we’ll follow up.
      </Typography>

      {sent ? (
        <Box>
          <Alert severity="success" sx={{ mb: 2 }}>
            Thanks, we’ve got it.
            {sent.training ? ' We’ll add you to the training group chat.' : ''}
            {sent.league ? ' You’re on the adult league list.' : ''}
            {sent.info ? ' We’ll reply to your question by email.' : ''}
          </Alert>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            {sent.member && (
              <Button variant="contained" onClick={() => navigate('/adult-membership')}
                sx={{ backgroundColor: '#1E3A5F', '&:hover': { backgroundColor: '#2c4a7c' } }}>
                See member plans
              </Button>
            )}
            <Button variant="text" onClick={() => setSent(null)} sx={{ color: '#888' }}>Change my answers</Button>
          </Box>
        </Box>
      ) : (
        <>
          {OPTIONS.map(o => (
            <Box key={o.key}>
              <FormControlLabel
                control={<Checkbox checked={picked[o.key]}
                  onChange={e => setPicked(p => ({ ...p, [o.key]: e.target.checked }))} />}
                label={<Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E3A5F' }}>{o.label}</Typography>
                  <Typography variant="caption" sx={{ color: '#888' }}>{o.hint}</Typography>
                </Box>}
                sx={{ alignItems: 'flex-start', mb: 1, '& .MuiCheckbox-root': { pt: 0.5 } }}
              />
              {o.key === 'info' && picked.info && (
                <TextField fullWidth multiline minRows={2} label="What would you like to know?"
                  value={question} onChange={e => setQuestion(e.target.value)} sx={{ mb: 1.5, ml: { sm: 4 }, maxWidth: { sm: 'calc(100% - 32px)' } }} />
              )}
            </Box>
          ))}
          {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
          <Button variant="contained" disabled={busy || !any || (picked.info && !question.trim())} onClick={submit}
            sx={{ backgroundColor: '#C9A84C', color: '#0D1E35', fontWeight: 700, '&:hover': { backgroundColor: '#b8963f' } }}>
            {busy ? <CircularProgress size={20} sx={{ color: '#0D1E35' }} /> : 'Send'}
          </Button>
          {onDismiss && (
            <Button variant="text" disabled={busy} onClick={onDismiss} sx={{ ml: 1, color: '#888' }}>Not now</Button>
          )}
        </>
      )}
    </Paper>
  );
}
